import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';

export type PipelineStatus =
  | 'lead'
  | 'consultation'
  | 'consent_sent'
  | 'confirmed'
  | 'in_session'
  | 'retouch'
  | 'completed'
  | 'lost';

export interface PipelineClient {
  id: string;
  owner_id: string;
  first_name: string;
  last_name: string;
  name: string;
  pipeline_status: PipelineStatus;
  style_preferences: string[];
  has_allergy: boolean;
  photo_url: string | null;
}

export interface PipelineColumnConfig {
  id: PipelineStatus;
  label: string;
  dotColor: string;
  ringColor: string;
}

export const PIPELINE_STATUSES: PipelineStatus[] = [
  'lead', 'consultation', 'consent_sent', 'confirmed',
  'in_session', 'retouch', 'completed', 'lost',
];

export const PIPELINE_COLUMNS: PipelineColumnConfig[] = [
  { id: 'lead',         label: 'Leads',                dotColor: 'bg-slate-400',  ringColor: 'ring-slate-200'  },
  { id: 'consultation', label: 'Consultation',          dotColor: 'bg-blue-500',   ringColor: 'ring-blue-200'   },
  { id: 'consent_sent', label: 'Consentement envoyé',   dotColor: 'bg-amber-500',  ringColor: 'ring-amber-200'  },
  { id: 'confirmed',    label: 'RDV confirmé',          dotColor: 'bg-violet-500', ringColor: 'ring-violet-200' },
  { id: 'in_session',   label: 'Session en cours',      dotColor: 'bg-orange-500', ringColor: 'ring-orange-200' },
  { id: 'retouch',      label: 'Retouche',              dotColor: 'bg-pink-500',   ringColor: 'ring-pink-200'   },
  { id: 'completed',    label: 'Terminé',               dotColor: 'bg-green-500',  ringColor: 'ring-green-200'  },
];

export function usePipeline() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['pipeline', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<PipelineClient[]> => {
      const [clientsRes, medicalsRes] = await Promise.all([
        (supabase as any)
          .from('clients')
          .select('id, owner_id, first_name, last_name, photo_url, pipeline_status, style_preferences')
          .eq('owner_id', user!.id)
          .eq('archived', false)
          .order('created_at', { ascending: false }),
        (supabase as any)
          .from('client_medical')
          .select('client_id, allergies')
          .eq('owner_id', user!.id),
      ]);

      if (clientsRes.error) throw clientsRes.error;

      const allergySet = new Set<string>(
        ((medicalsRes.data as any[]) || [])
          .filter((m: any) => Array.isArray(m.allergies) && m.allergies.length > 0)
          .map((m: any) => m.client_id as string),
      );

      return ((clientsRes.data as any[]) || []).map((c: any): PipelineClient => ({
        id: c.id,
        owner_id: c.owner_id || '',
        first_name: c.first_name || '',
        last_name: c.last_name || '',
        name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—',
        pipeline_status: (c.pipeline_status as PipelineStatus) || 'lead',
        style_preferences: (c.style_preferences as string[]) || [],
        has_allergy: allergySet.has(c.id),
        photo_url: c.photo_url || null,
      }));
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ clientId, status }: { clientId: string; status: PipelineStatus }) => {
      const { error } = await (supabase as any)
        .from('clients')
        .update({ pipeline_status: status })
        .eq('id', clientId)
        .eq('owner_id', user!.id);
      if (error) throw error;
    },
    onMutate: async ({ clientId, status }) => {
      await qc.cancelQueries({ queryKey: ['pipeline', user?.id] });
      const prev = qc.getQueryData<PipelineClient[]>(['pipeline', user?.id]);
      qc.setQueryData<PipelineClient[]>(['pipeline', user?.id], old =>
        (old || []).map(c => c.id === clientId ? { ...c, pipeline_status: status } : c),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['pipeline', user?.id], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['pipeline', user?.id] }),
  });

  const clients = query.data || [];

  const columns = PIPELINE_STATUSES.reduce((acc, s) => {
    acc[s] = clients.filter(c => c.pipeline_status === s);
    return acc;
  }, {} as Record<PipelineStatus, PipelineClient[]>);

  return {
    ...query,
    clients,
    columns,
    updatePipelineStatus: (clientId: string, status: PipelineStatus) =>
      mutation.mutate({ clientId, status }),
  };
}
